import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  InGameCurrency,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { BodyM, BrandM } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { CardCollectionTask } from '#src/components/missions/CardCollectionTask/index.tsx';
import {
  TutorialStepAsset,
  TutorialStepContent,
  TutorialStepTextContent,
} from '#src/components/missions/CommonMissionsTutorialDialog/ui.tsx';

const Content = styled(TutorialStepContent)`
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(to bottom, transparent 0%, var(--c-black) 100%);
  }
`;

const StyledNeonEdge = styled(NeonEdge)`
  position: absolute;
  inset: 0;
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const TaskWrapper = styled(TutorialStepAsset)`
  filter: drop-shadow(0 var(--unit) var(--unit) var(--c-limited));
`;

const messages = defineMessages({
  nbaCardCollectionTaskDescription: {
    defaultMessage: 'Own Common Cards from NBA',
  },
  plCardCollectionTaskDescription: {
    defaultMessage: 'Own Common Cards from Premier League',
  },
});

export const CompleteMiletones = () => {
  const { formatMessage } = useIntl();
  const { sport } = useSportContext();
  if (sport === Sport.BASEBALL) {
    return null;
  }
  return (
    <Bezel borderColor="var(--c-nd-150)">
      <StyledTextureBackground
        background="missions"
        mode="dark"
        color={['transparent', 'var(--c-limited)']}
        imageOpacity={0.3}
        withDots
      />
      <Content gap={2}>
        <TaskWrapper>
          <CardCollectionTask
            cardCollectionTask={{
              __typename: 'CardsCountTask',
              id: '1',
              title: sport === Sport.NBA ? 'NBA' : 'Premier League',
              description:
                sport === Sport.NBA
                  ? formatMessage(messages.nbaCardCollectionTaskDescription)
                  : formatMessage(messages.plCardCollectionTaskDescription),
              liveScore: 35,
              iconUrl:
                sport === Sport.NBA
                  ? null
                  : // Premier League icon
                    'https://assets.sorare.com/competition/c61cbd9a-7ecc-4c0b-b428-08d54410fa54/picture/8f24da96a6cd84035f19b2fe434e78b3.png',
              thresholds: [
                {
                  count: 50,
                  current: true,
                  rewardConfigs: [],
                  __typename: 'CollectionTaskThreshold',
                },
              ],
              color: 'var(--c-limited)',
              rewardConfigs: [
                {
                  amount: 100,
                  currency: InGameCurrency.LIMITED_XP,
                  __typename: 'InGameCurrencyRewardConfig',
                  id: '1',
                },
              ],
              countTaskRarity: Rarity.common,
              templateIconUrl:
                'https://frontend-assets.sorare.com/task_configs/cards_count/icon.png',
            }}
          />
        </TaskWrapper>
        <TutorialStepTextContent>
          <BrandM>
            <FormattedMessage defaultMessage="Complete Milestones" />
          </BrandM>
          <BodyM color="var(--c-nd-600)">
            <FormattedMessage defaultMessage="to get valuable bonuses and rewards" />
          </BodyM>
        </TutorialStepTextContent>
      </Content>
      <StyledNeonEdge
        color="var(--c-limited-primary)"
        showNeon="always"
        showHalo="always"
      />
    </Bezel>
  );
};
